'use client';

import { useQuery } from '@tanstack/react-query';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/components/AuthProvider';

export interface AccountantProfile {
  user_id: string;
  user_type: 'standard' | 'accountant';
  display_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface AccountantBusinessAssignment {
  id: string;
  accountant_user_id: string;
  business_id: string;
  permissions: {
    files: boolean;
    integrations: boolean;
    chat: boolean;
  };
  created_at: string;
  updated_at: string;
}

export interface AssignedBusiness {
  id: string;
  name: string;
  description?: string;
  industry?: string;
  website?: string;
  phone?: string;
  email?: string;
  business_type?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Hook to check if the current user is an accountant
 */
export function useIsAccountant() {
  const { user, isLoading: isAuthLoading } = useAuth();
  const supabase = createClient();

  return useQuery({
    queryKey: ['user-profile', 'is-accountant', user?.id],
    queryFn: async (): Promise<boolean> => {
      if (!user) return false;

      // First check user_metadata for quick check
      const userMetadata = user.user_metadata;
      if (userMetadata?.user_type === 'accountant') {
        return true;
      }

      // Then check the database profile
      const { data, error } = await supabase
        .from('user_profiles')
        .select('user_type')
        .eq('user_id', user.id)
        .single();

      if (error) {
        // If table doesn't exist or no profile, user is not an accountant
        return false;
      }

      return data?.user_type === 'accountant';
    },
    enabled: !isAuthLoading && !!user,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Hook to get the user profile including accountant status
 */
export function useUserProfile() {
  const { user, isLoading: isAuthLoading } = useAuth();
  const supabase = createClient();

  return useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: async (): Promise<AccountantProfile | null> => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) {
        // Return a default profile if not found
        return {
          user_id: user.id,
          user_type: 'standard',
          display_name: user.user_metadata?.display_name || user.email?.split('@')[0] || null,
          created_at: user.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
      }

      return data;
    },
    enabled: !isAuthLoading && !!user,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to get businesses assigned to an accountant user
 */
export function useAccountantBusinesses() {
  const { user, isLoading: isAuthLoading } = useAuth();
  const { data: isAccountant, isLoading: isCheckingAccountant } = useIsAccountant();
  const supabase = createClient();

  return useQuery({
    queryKey: ['accountant-businesses', user?.id],
    queryFn: async (): Promise<AssignedBusiness[]> => {
      if (!user || !isAccountant) return [];

      // Get assigned business IDs
      const { data: assignments, error: assignmentError } = await supabase
        .from('accountant_business_assignments')
        .select('business_id, permissions')
        .eq('accountant_user_id', user.id);

      if (assignmentError || !assignments || assignments.length === 0) {
        return [];
      }

      // Get business details
      const businessIds = assignments.map(a => a.business_id);
      const { data: businesses, error: businessError } = await supabase
        .from('businesses')
        .select('*')
        .in('id', businessIds)
        .is('deleted_at', null);

      if (businessError || !businesses) {
        return [];
      }

      return businesses;
    },
    enabled: !isAuthLoading && !isCheckingAccountant && !!user && isAccountant === true,
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
  });
}

/**
 * Hook to get accountant permissions for a specific business
 */
export function useAccountantPermissions(businessId: string | undefined) {
  const { user, isLoading: isAuthLoading } = useAuth();
  const { data: isAccountant, isLoading: isCheckingAccountant } = useIsAccountant();
  const supabase = createClient();

  return useQuery({
    queryKey: ['accountant-permissions', user?.id, businessId],
    queryFn: async () => {
      if (!user || !isAccountant || !businessId) {
        return { files: true, integrations: true, chat: true }; // Default permissions for non-accountants
      }

      const { data, error } = await supabase
        .from('accountant_business_assignments')
        .select('permissions')
        .eq('accountant_user_id', user.id)
        .eq('business_id', businessId)
        .single();

      if (error || !data) {
        return { files: false, integrations: false, chat: false };
      }

      return data.permissions;
    },
    enabled: !isAuthLoading && !isCheckingAccountant && !!user && !!businessId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Composite hook that provides all accountant-related data
 */
export function useAccountant() {
  const { user, isLoading: isAuthLoading } = useAuth();
  const { data: isAccountant, isLoading: isCheckingAccountant } = useIsAccountant();
  const { data: profile, isLoading: isLoadingProfile } = useUserProfile();
  const { data: assignedBusinesses, isLoading: isLoadingBusinesses } = useAccountantBusinesses();

  return {
    // User info
    user,
    profile,
    
    // Accountant status
    isAccountant: isAccountant === true,
    
    // Assigned businesses (only for accountants)
    assignedBusinesses: assignedBusinesses || [],
    
    // Loading states
    isLoading: isAuthLoading || isCheckingAccountant || isLoadingProfile || (isAccountant && isLoadingBusinesses),
    isAuthLoading,
    isCheckingAccountant,
    isLoadingProfile,
    isLoadingBusinesses: isAccountant ? isLoadingBusinesses : false,
    
    // Computed
    hasAssignedBusinesses: (assignedBusinesses?.length || 0) > 0,
  };
}
